import _ from 'lodash';
import {requiredAndInvalidProductFieldsByType} from '@treez/commons/utils/product';
import {ImageAttributes} from '@treez/commons/sharedTypings';
import {
    Catalog,
    ExtractionMethod,
    OwnerType,
    ProductAttributes,
    ProductAttributesField,
    ProductData,
    ProductDescription,
    ProductDetails,
    ProductDetailsField,
    ProductField,
    ProductInCatalog,
    ProductType,
    UOM,
} from '@treez/commons/sharedTypings/product';
import React, {FunctionComponent, RefObject, useEffect, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import {Grid, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs} from '@material-ui/core';
import {
    Form,
    InputErrorStatus,
    NumberInput,
    NumericalUnit,
    ProductClassificationSelect,
    ProductSubtypeSelect,
    ProductTypeSelect,
    SelectInput,
    TagInput,
    TextInput,
    TreezSwitch,
} from '@treez-ui/core/forms';
import {Section, Title} from '@treez-ui/core/layout';
import {TitleSize} from '@treez-ui/core/layout/Title';
import {ModalType, SnackbarType} from '@treez-ui/core/feedback/modals';
import {Color} from '@treez-ui/core/styles/Color';
import {DropdownIcon} from '@treez-ui/core/icons';
import {Button, ButtonClass} from '@treez-ui/core/buttons';
import {PriceTier} from '@treez/commons/sharedTypings/priceTier';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import {
    getStoreProductValidationResult,
    ProductValidationErrorReport,
    StoreProductValidationResult,
} from '~/lib/errors';
import {
    cleanProductToEdit,
    EACH_PRODUCT_TYPES,
    getOverriddenValue,
    getPathToField,
} from '~/lib/product';
import ImageGui from '~/modules/widgets/ImageGui';
import {clearSnackbar, showSnackbar} from '~/redux/actions/SnackbarActions';
import {
    getBrandProductsById,
    getCatalogById,
    getCatalogProductData,
    getValidUomOptions,
    ProductStorePresenceAttributes,
    updateCatalogProduct,
} from '~/utils/ProductAPI';
import {ClearOverridePopover} from '~/modules/widgets/ClearOverridePopover';
import {StoreAttributes} from '~/lib/directory';
import PriceTierLabel from '../widgets/PriceTierLabel';
import {formatCurrency} from '~/lib/format';
import {
    closeFeedbackModal,
    openFeedbackModal,
} from '~/redux/boundActionCreators/UserFeedbackActions';
import CatalogHistory from '../widgets/CatalogHistory';

const {INGREDIENT, FLAVOR, GENERAL, INTERNAL, EFFECT} = ProductAttributesField;

const {
    AMOUNT,
    ATTRIBUTES,
    BRAND_NAME,
    CLASSIFICATION,
    DESCRIPTIONS,
    DETAILS,
    ECOMMERCE_NAME,
    EXTERNAL_ID,
    NAME,
    PRICE,
    SIZE,
    SUBTYPE,
    TYPE,
    SKU,
    VISIBLE,
} = ProductField;

const {
    CBD_MG,
    DOSE_CBD_MG,
    DOSE_THC_MG,
    DOSES,
    GROSS_WEIGHT,
    NET_WEIGHT,
    STRAIN,
    THC_MG,
    TOTAL_WEIGHT,
    TOTAL_CONCENTRATE_WEIGHT
} = ProductDetailsField;

export interface ProductInputDataOptions {
    flavorAttributesOptions: string[];
    generalAttributesOptions: string[];
    effectAttributesOptions: string[];
    ingredientAttributesOptions: string[];
    internalAttributesOptions: string[];
    brandOptions: string[];
}

export enum CatalogType {
    BRAND_CATALOG = 'BRAND_CATALOG',
    CENTRAL_CATALOG = 'CENTRAL_CATALOG',
    STORE_CATALOG = 'STORE_CATALOG',
}

export enum ProductFormSection {
    PRODUCT_ATTRIBUTES,
    PRODUCT_DETAILS,
    PRODUCT_INFORMATION,
    COMMERCE_SECTION,
    PRICING_SECTION,
}

const {BRAND_CATALOG, CENTRAL_CATALOG, STORE_CATALOG} = CatalogType;

const NON_CANNABIS_TYPES = [ProductType.CBD, ProductType.MERCH, ProductType.NON_INV];

enum PriceType {
    FLAT = 'Flat Price',
    TIER = 'Tier Price',
}

const PRICE_SELECT_OPTIONS = [PriceType.FLAT, PriceType.TIER];

const DEFAULT_PRICE_TYPE = PriceType.FLAT;

export interface ProductFormProps {
    addProduct: (productToAdd: Partial<ProductInCatalog>) => void;
    catalogId: number;
    catalogType: CatalogType;
    editProduct: (
        productToEdit: Partial<ProductInCatalog>,
        overridesToClear: ProductData[]
    ) => void;
    handleCancel: (draftProduct: Partial<ProductInCatalog>) => void;
    handleOpenProductValidationFeedback: (
        storeProductValidationErrorResult: StoreProductValidationResult[]
    ) => void;
    priceTiers?: PriceTier[];
    productDataOptions: ProductInputDataOptions;
    productToEdit?: ProductInCatalog | null;
    scrollTo?: ProductFormSection | null;
    stores?: StoreAttributes[];
}

export const ProductForm: FunctionComponent<ProductFormProps> = (props: ProductFormProps) => {
    const {
        catalogId,
        productToEdit,
        productDataOptions,
        addProduct,
        editProduct,
        handleCancel,
        handleOpenProductValidationFeedback,
        catalogType,
        priceTiers,
        stores,
        scrollTo,
    } = props;

    const {
        flavorAttributesOptions,
        generalAttributesOptions,
        effectAttributesOptions,
        ingredientAttributesOptions,
        internalAttributesOptions,
    } = productDataOptions;

    const productImages = _.get(productToEdit, ProductField.IMAGES) || [];

    const [product, setProduct] = useState<Partial<ProductInCatalog>>({});
    const [catalog, setCatalog] = useState<Catalog>();

    const [productType, setProductType] = useState<ProductType>();
    const [productSubtype, setProductSubtype] = useState<string>();

    const [requiredFields, setRequiredFields] = useState<ProductField[]>([]);
    const [invalidFields, setInvalidFields] = useState<ProductField[]>([]);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);
    const [fieldErrors, setFieldErrors] = useState<ProductValidationErrorReport>(
        {} as ProductValidationErrorReport
    );

    const [uploadedImages, setUploadedImages] = useState<ImageAttributes[]>(productImages);
    const [linkedBrandProduct, setLinkedBrandProduct] = useState<ProductInCatalog | undefined>();
    const [overridesToClear, setOverridesToClear] = useState<ProductData[]>([]);

    const [activeProductStorePresence, setActiveProductStorePresence] = useState<
        ProductStorePresenceAttributes[]
    >([]);
    const [assignedPrices, setAssignedPrices] = useState<ProductStorePresenceAttributes[]>();
    const [priceInput, setPriceInput] = useState<string>('');
    const [selectedPriceTier, setSelectedPriceTier] = useState<number>();
    const [hasInitiallyScrolledToSection, setHasInitiallyScrolledToSection] =
        useState<boolean>(false);

    const [priceType, setPriceType] = useState<PriceType>(DEFAULT_PRICE_TYPE);
    const [tabValue, setTabValue] = React.useState(0);
    const [disabledButtons, setDisabledButtons] = useState<Record<string, boolean>>({});

    const handleHistoryTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const isNew = !product.id;

    const dispatch = useDispatch();
    // State Management
    const checkErrorsOnInput = async () => {
        const errorsPresent = !_.isEmpty(fieldErrors);

        const productInputs = {
            ...product,
            type: productType,
            subtype: productSubtype,
            ...product.details,
        };

        const emptyRequiredFields = _.filter(requiredFields, (field) => {
            const requiredFieldValue = productInputs[field];

            return requiredFieldValue == null || requiredFieldValue === '';
        });

        if (errorsPresent || _.size(emptyRequiredFields) > 0) {
            return false;
        }
        return true;
    };

    const clearDraftProductField = (field: ProductField, subField?: string) => {
        if (subField == null) {
            setProduct({
                ...product,
                [field]: null,
            });
        } else {
            const newJSONField = _.omit(product, subField);
            setProduct({
                ...product,
                [field]: newJSONField,
            });
        }
    };

    const handleErrorOnInput = (field: ProductData, inputErrorInfo: InputErrorStatus) => {
        const {errorStatus, errorMessage} = inputErrorInfo;

        const newErrorReport = errorStatus
            ? {
                  ...fieldErrors,
                  [field]: errorMessage,
              }
            : _.omit(fieldErrors, [field]);

        setFieldErrors(newErrorReport as ProductValidationErrorReport);
    };

    const submitFormData = async () => {
        setHasBeenSubmitted(true);

        const catalogIdsThatCarryProduct = (activeProductStorePresence || [])
            .filter((presence) => presence.status === 'ACTIVE')
            .map((presence) => presence.id);

        const storeProductValidationErrorResult: StoreProductValidationResult[] =
            getStoreProductValidationResult(product, catalogIdsThatCarryProduct, stores, dispatch);

        if (storeProductValidationErrorResult.length > 0) {
            handleOpenProductValidationFeedback(storeProductValidationErrorResult);
            return;
        }

        const noErrorsPresent = await checkErrorsOnInput();

        try {
            if (noErrorsPresent) {
                if (productType === ProductType.MERCH) {
                    // Temporary fix: product-api only accepts Merch products with cannabis false
                    product.cannabis = false;
                }

                if (productToEdit?.id) {
                    await editProduct(
                        {
                            ...product,
                            type: productType,
                            subtype: productSubtype,
                        },
                        overridesToClear
                    );
                } else {
                    await addProduct({
                        ...product,
                        type: productType,
                        subtype: productSubtype,
                        uom: product.uom ? product.uom : UOM.EACH,
                    });
                }
                await submitAssignedPrices();
            } else {
                console.error(fieldErrors);
                dispatch(
                    showSnackbar({
                        onClose: () => dispatch(clearSnackbar()),
                        type: SnackbarType.WARNING,
                        content: `Errors found in product, please see below and fill in all required information`,
                    })
                );
                return;
            }
        } catch (error) {
            console.error(error);
            openFeedbackModal(closeFeedbackModal, 'Error Saving Product', {
                type: ModalType.ERROR,
                bodyText: 'Unable to save Product',
                cancelOption: true,
                onCancel: () => closeFeedbackModal(),
            });
        }
    };

    const submitAssignedPrices = async () => {
        if (!assignedPrices || !productToEdit?.id) {
            return;
        }

        await Promise.all(
            assignedPrices.map((productPriceSetting) => {
                const {id: catalogId, price, priceTierId} = productPriceSetting;

                const priceWithoutUndefinedValues = _.omitBy({price, priceTierId}, _.isUndefined);

                return updateCatalogProduct(
                    catalogId,
                    productToEdit?.id,
                    priceWithoutUndefinedValues
                );
            })
        );
    };

    const updateDraftProduct = (
        field: ProductField,
        value: string | number | boolean | undefined
    ) => {
        if (value == null || value === '') {
            setProduct({
                ...product,
                [field]: undefined,
            });
        } else {
            setProduct({
                ...product,
                [field]: value,
            });
        }

        if (_.includes(overridesToClear, field)) {
            setOverridesToClear(_.without(overridesToClear, field));
        }
    };

    const updateJSONField = (
        field: ProductField,
        subField: string,
        value: string[] | string | number | undefined
    ) => {
        const previousValues = product[field] as object;
        if (value == null || value === '') {
            setProduct({
                ...product,
                [field]: {
                    ...previousValues,
                    [subField]: null,
                },
            });
        } else {
            setProduct({
                ...product,
                [field]: {
                    ...previousValues,
                    [subField]: value,
                },
            });
        }

        if (field === DESCRIPTIONS && _.includes(overridesToClear, DESCRIPTIONS)) {
            setOverridesToClear(_.without(overridesToClear, DESCRIPTIONS));
        }
        if (field !== DESCRIPTIONS && _.includes(overridesToClear, subField)) {
            setOverridesToClear(_.without(overridesToClear, subField as ProductData));
        }
    };

    const clearFieldOverride = async (field: ProductData, newValue: any) => {
        if (field === DESCRIPTIONS) {
            updateJSONField(DESCRIPTIONS, 'main', newValue);
        } else if (_.includes(ProductAttributesField, field)) {
            updateJSONField(ATTRIBUTES, field, newValue);
        } else if (_.includes(ProductDetailsField, field)) {
            updateJSONField(DETAILS, field, newValue);
        } else {
            updateDraftProduct(field as ProductField, newValue);
        }

        if (_.includes(overridesToClear, field) == false) {
            setOverridesToClear([...overridesToClear, ...[field]]);
        }
    };

    const attributes = _.get(product, ATTRIBUTES) as ProductAttributes | undefined;
    const details = _.get(product, DETAILS) as ProductDetails;
    const descriptions = _.get(product, DESCRIPTIONS) as ProductDescription;

    const {
        amount,
        brandName,
        classification,
        eCommerceName,
        externalId,
        images = [],
        name,
        ownerType: productOwnerType = OwnerType.STORE,
        price,
        size,
        sku,
        uom,
    } = product;

    const effect = _.get(attributes, EFFECT, []) || [];
    const flavor = _.get(attributes, FLAVOR, []) || [];
    const general = _.get(attributes, GENERAL, []) || [];
    const ingredient = _.get(attributes, INGREDIENT, []) || [];
    const internal = _.get(attributes, INTERNAL, []) || [];

    const main = _.get(descriptions, 'main', '') || '';

    const cbdMg = _.get(details, CBD_MG);
    const doseCbdMg = _.get(details, DOSE_CBD_MG);
    const doses = _.get(details, DOSES);
    const doseThcMg = _.get(details, DOSE_THC_MG);
    const extractionMethod = _.get(details, ProductDetailsField.EXTRACTION_METHOD);
    const grossWeight = _.get(details, GROSS_WEIGHT);
    const netWeight = _.get(details, NET_WEIGHT);
    const strain = _.get(details, STRAIN);
    const thcMg = _.get(details, THC_MG);
    const totalWeight = _.get(details, TOTAL_WEIGHT);
    const totalConcentrateWeight = _.get(details, TOTAL_CONCENTRATE_WEIGHT);

    // Lifecycle Methods
    useEffect(() => {
        if (catalogId) {
            getCatalogById(catalogId)
                .then((fetchedCatalog) => {
                    setCatalog(fetchedCatalog);
                })
                .catch((error) => {
                    console.error(error);
                });
        }
    }, []);

    useEffect(() => {
        if (images && images.length > 0) {
            setUploadedImages(images);
        }
    }, [images]);

    useEffect(() => {
        if (productToEdit) {
            setProduct(productToEdit);
            setProductType(productToEdit.type);
            setProductSubtype(productToEdit.subtype);
        } else {
            setProduct({});
        }
    }, [productToEdit]);

    useEffect(() => {
        if (productToEdit && productToEdit?.id && stores) {
            const catalogIds = _.map(stores, (store) => {
                return store.catalogId;
            }) as number[];

            getCatalogProductData(catalogIds, [productToEdit?.id]).then((storePresence) => {
                const productStorePresence = storePresence[productToEdit?.id];
                const storeMap = new Map(stores.map((store) => [store.catalogId, store]));

                const activeProductStorePresence: ProductStorePresenceAttributes[] = [];

                productStorePresence.forEach((presence) => {
                    if (storeMap.has(presence.id)) {
                        const store = storeMap.get(presence.id) as StoreAttributes;
                        activeProductStorePresence.push({
                            ...presence,
                            storeName: store.name,
                        });
                    }
                });

                setActiveProductStorePresence(activeProductStorePresence);
            });
        }
    }, [productToEdit, stores]);

    useEffect(() => {
        const productWithUpdatedImages = {
            ...product,
            images: uploadedImages,
        };

        setProduct(productWithUpdatedImages);
    }, [uploadedImages]);

    useEffect(() => {
        const newProduct = {...product};

        if (productType && EACH_PRODUCT_TYPES.includes(productType)) {
            newProduct.uom = UOM.EACH;
        }

        if (productType && NON_CANNABIS_TYPES.includes(productType)) {
            newProduct.cannabis = false;
        } else {
            newProduct.cannabis = true;
        }

        setProduct(newProduct);

        if (productType) {
            const productFields = requiredAndInvalidProductFieldsByType(
                productType,
                productSubtype
            );
            setRequiredFields(productFields.required as ProductField[]);
            setInvalidFields(productFields.invalid as ProductField[]);
        } else {
            setRequiredFields([NAME, TYPE, ProductField.UOM]);
            setInvalidFields([]);
        }
    }, [productType, productSubtype]);

    useEffect(() => {
        if (productToEdit) {
            setProduct(cleanProductToEdit(productToEdit));
            setProductType(productToEdit.type);
            setProductSubtype(productToEdit.subtype);
        }
        if (productToEdit?.linkedTo) {
            const brandProductId = Number(productToEdit.linkedTo);

            if (isNaN(brandProductId) === false) {
                fetchBrandProduct(brandProductId);
            }
        }
    }, [productToEdit]);

    // API Calls
    const fetchBrandProduct = async (brandProductId: number) => {
        const brandProducts = await getBrandProductsById([brandProductId]);

        if (_.size(brandProducts) > 0) {
            const brandProduct: ProductInCatalog = brandProducts[0];

            setLinkedBrandProduct(brandProduct);
        }
    };

    // references && Render Functions
    const COMMERCE_SECTION = useRef<HTMLDivElement>(null);
    const PRODUCT_ATTRIBUTES = useRef<HTMLDivElement>(null);
    const PRODUCT_DETAILS = useRef<HTMLDivElement>(null);
    const PRODUCT_INFORMATION = useRef<HTMLDivElement>(null);
    const PRICING_SECTION = useRef<HTMLDivElement>(null);
    const HISTORY_SECTION = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!_.isUndefined(scrollTo) && !hasInitiallyScrolledToSection) {
            let sectionToScrollTo: React.RefObject<HTMLDivElement> | null = null;

            switch (scrollTo) {
                case ProductFormSection.PRICING_SECTION:
                    sectionToScrollTo = PRICING_SECTION;
                    break;
                case ProductFormSection.PRODUCT_ATTRIBUTES:
                    sectionToScrollTo = PRODUCT_ATTRIBUTES;
                    break;
                case ProductFormSection.PRODUCT_DETAILS:
                    sectionToScrollTo = PRODUCT_DETAILS;
                    break;
                case ProductFormSection.PRODUCT_INFORMATION:
                    sectionToScrollTo = PRODUCT_INFORMATION;
                    break;
                case ProductFormSection.COMMERCE_SECTION:
                    sectionToScrollTo = COMMERCE_SECTION;
                    break;

                default:
                    break;
            }

            if (sectionToScrollTo && sectionToScrollTo.current !== null) {
                scrollToSection(sectionToScrollTo);
                setHasInitiallyScrolledToSection(true);
            }
        }
    });

    const isFieldRequired = (fieldName: string) => {
        return _.includes(requiredFields, fieldName);
    };

    const isFieldInvalid = (fieldName: string) => {
        return _.includes(invalidFields, fieldName);
    };

    const scrollToSection = (section: RefObject<HTMLDivElement>) => {
        if (section.current) {
            section.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    };

    const isFieldOverridden = (fieldName: ProductData) => {
        // Want Stephen to walk me through this code
        if (catalog == null) {
            return false;
        }

        const {catalogOverrides, productOverrides} = product;

        const path = getPathToField(fieldName);
        const isLinked = Boolean(linkedBrandProduct);

        const productIsntOwnedByEditor =
            catalog.ownerType !== productOwnerType && catalog.ownerId !== Number(product.ownerId);

        const canHaveOverrides = isLinked || productIsntOwnedByEditor;

        const hasCatalogOverrides = _.has(catalogOverrides, path);
        const hasProductOverrides = _.has(productOverrides, path);

        if (
            !isNew &&
            canHaveOverrides &&
            !_.isEqual(_.get(productToEdit, path), _.get(product, path))
        ) {
            return true;
        }

        return hasCatalogOverrides || hasProductOverrides;
    };

    const getDropdownIndicator = (fieldName: ProductData) => {
        const isFieldOverrideCleared = _.includes(overridesToClear, fieldName);

        if (isFieldOverridden(fieldName) && isFieldOverrideCleared == false) {
            return () => renderDropdownIndicator(fieldName);
        }
    };

    const getEndAdornment = (fieldName: ProductData) => {
        const isFieldOverrideCleared = _.includes(overridesToClear, fieldName);

        if (isFieldOverridden(fieldName) && isFieldOverrideCleared == false) {
            return renderOverrideLink(fieldName);
        }
    };

    const renderDropdownIndicator = (fieldName) => {
        return (
            <>
                <DropdownIcon color={Color.GREY_LIGHT} />
                {renderOverrideLink(fieldName)}
            </>
        );
    };

    const renderOverrideLink = (fieldName) => {
        const value = getOverriddenValue(product, linkedBrandProduct, fieldName);

        let displayValue = value;
        if (_.includes(ProductAttributesField, fieldName)) {
            displayValue = _.join(value, ', ');
        }

        return (
            <ClearOverridePopover
                onSubmit={() => clearFieldOverride(fieldName, value)}
                value={displayValue}
            />
        );
    };

    const getPriceFieldLabel = () => {
        // Eventually to be refined;
        switch (catalogType) {
            case CENTRAL_CATALOG:
                return 'Central Catalog Price';
            case BRAND_CATALOG:
                return 'Price';
            case STORE_CATALOG:
                return 'Price';
            default:
                return 'Price';
        }
    };

    const getPriceForStore = (productStorePresence: ProductStorePresenceAttributes) => {
        const {price, priceTierId} = productStorePresence;

        if (priceTierId) {
            return <PriceTierLabel tierId={priceTierId} />;
        }

        return formatCurrency(price || '0.00');
    };

    const getPriceInputElement = (currentPriceType: PriceType) => {
        if (currentPriceType === PriceType.FLAT) {
            return (
                <NumberInput
                    type="number"
                    onChange={(value) => {
                        setPriceInput(value as string);
                        setDisabledButtons({});
                    }}
                    startAdornment="$"
                    value={priceInput}
                />
            );
        }

        const populatedPriceTiers = priceTiers || [];

        const priceTierOptions = populatedPriceTiers.map((priceTier) => `${priceTier.label}`);

        return (
            <SelectInput
                options={priceTierOptions}
                placeholder="Select Price Tier"
                value={undefined}
                onClear={() => {}}
                onChange={(value, index) => {
                    if (!_.isUndefined(index)) {
                        const priceTier = populatedPriceTiers[index];
                        if (priceTier) {
                            setSelectedPriceTier(priceTier.id);
                        }
                    }
                }}
            />
        );
    };

    const assignPriceToStore = (
        productStorePresence: ProductStorePresenceAttributes,
        index: number,
        priceType: PriceType
    ) => {
        if (priceType === PriceType.FLAT) {
            if (isValidPrice(priceInput)) {
                const alreadyAssignedPrices = assignedPrices || [];

                const price = _.isEmpty(priceInput) ? '0.00' : priceInput;
                const newProductStorePresence = {
                    ...productStorePresence,
                    priceTierId: null,
                    price,
                } as ProductStorePresenceAttributes;

                const newActiveStorePresences = [...activeProductStorePresence];
                newActiveStorePresences[index] = newProductStorePresence;
                setActiveProductStorePresence(newActiveStorePresences);

                setAssignedPrices([
                    ..._.reject(alreadyAssignedPrices, {id: productStorePresence.id}),
                    newProductStorePresence,
                ]);
            }
        } else if (priceType === PriceType.TIER) {
            const alreadyAssignedPrices = assignedPrices || [];
            const newProductStorePresence = {
                ...productStorePresence,
                priceTierId: selectedPriceTier,
            } as ProductStorePresenceAttributes;

            const newActiveStorePresences = [...activeProductStorePresence];
            newActiveStorePresences[index] = newProductStorePresence;
            setActiveProductStorePresence(newActiveStorePresences);

            setAssignedPrices([
                ..._.reject(alreadyAssignedPrices, {id: productStorePresence.id}),
                newProductStorePresence,
            ]);
        }
    };

    const isValidPrice = (price: string) => {
        if (_.isEmpty(price)) {
            return false;
        }

        const priceAsNumber = Number(price);
        if (_.isNaN(priceAsNumber) || priceAsNumber < 0) {
            openFeedbackModal(closeFeedbackModal, 'Invalid Price', {
                type: ModalType.WARNING,
                bodyText: 'Please assign a valid price that is not less than zero',
                cancelOption: true,
                onCancel: () => closeFeedbackModal(),
            });
            return false;
        }

        return true;
    };

    const shouldDisablePriceAssign = () => {
        if (priceType === PriceType.FLAT && _.isEmpty(priceInput)) {
            return true;
        }
        if (priceType === PriceType.TIER && _.isUndefined(selectedPriceTier)) {
            return true;
        }

        return false;
    };

    const assignPriceToAllStores = () => {
        if (priceType === PriceType.FLAT) {
            if (isValidPrice(priceInput)) {
                const price = _.isEmpty(priceInput) ? '0.00' : priceInput;
                const updatedProductStoresPresences = activeProductStorePresence.map(
                    (storePresence) => ({
                        ...storePresence,
                        priceTierId: null,
                        price,
                    })
                );

                setActiveProductStorePresence(
                    updatedProductStoresPresences as ProductStorePresenceAttributes[]
                );
                setAssignedPrices(
                    updatedProductStoresPresences as ProductStorePresenceAttributes[]
                );
            } else {
                setPriceInput('');
            }
        } else {
            const updatedProductStoresPresences = activeProductStorePresence.map(
                (storePresence) => ({
                    ...storePresence,
                    priceTierId: selectedPriceTier,
                })
            );

            setActiveProductStorePresence(
                updatedProductStoresPresences as ProductStorePresenceAttributes[]
            );
            setAssignedPrices(updatedProductStoresPresences as ProductStorePresenceAttributes[]);
        }
    };

    return (
        <div className="product-form">
            <div className="form-side-navigation-menu-container">
                <ul>
                    <li onClick={() => scrollToSection(PRODUCT_INFORMATION)}>
                        <Title size={TitleSize.SMALL} text="Product Information" />
                    </li>
                    <li onClick={() => scrollToSection(PRODUCT_DETAILS)}>
                        <Title size={TitleSize.SMALL} text="Product Details" />
                    </li>
                    {!isNew && (
                        <li onClick={() => scrollToSection(PRICING_SECTION)}>
                            <Title size={TitleSize.SMALL} text="Store Pricing" />
                        </li>
                    )}
                    <li onClick={() => scrollToSection(PRODUCT_ATTRIBUTES)}>
                        <Title size={TitleSize.SMALL} text="Product Attributes" />
                    </li>
                    <li onClick={() => scrollToSection(COMMERCE_SECTION)}>
                        <Title size={TitleSize.SMALL} text="Commerce" />
                    </li>
                    {!isNew && (
                        <li onClick={() => scrollToSection(HISTORY_SECTION)}>
                            <Title size={TitleSize.SMALL} text="History" />
                        </li>
                    )}
                </ul>
            </div>
            <Form
                stickyHeader
                onCancel={() =>
                    handleCancel({
                        ...product,
                        type: productType,
                        subtype: productSubtype,
                    })
                }
                onSave={() => submitFormData()}
                title={isNew ? 'Create New Product' : 'Edit Product'}
            >
                <Section title="Product Information">
                    <Grid container ref={PRODUCT_INFORMATION} spacing={6}>
                        <Grid item xs={12} sm={6}>
                            <ProductTypeSelect
                                disabled={!isNew}
                                required
                                dirty={hasBeenSubmitted}
                                handleError={(errorStatus, errorMessage) =>
                                    handleErrorOnInput(TYPE, {
                                        errorStatus,
                                        errorMessage,
                                    })
                                }
                                helperText={fieldErrors[TYPE]}
                                onChange={(optionSelected: ProductType) => {
                                    const productTypeCleaned = optionSelected
                                        ? (optionSelected.toUpperCase() as ProductType)
                                        : undefined;
                                    setProductType(productTypeCleaned);
                                }}
                                onClear={() => {
                                    setProductType(undefined);
                                    setProductSubtype(undefined);
                                }}
                                value={productType}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <ProductSubtypeSelect
                                dirty={hasBeenSubmitted}
                                disabled={!productType}
                                dropdownIndicator={getDropdownIndicator(SUBTYPE)}
                                handleError={(errorStatus, errorMessage) =>
                                    handleErrorOnInput(SUBTYPE, {
                                        errorStatus,
                                        errorMessage,
                                    })
                                }
                                helperText={fieldErrors[SUBTYPE]}
                                onChange={(value) => {
                                    setProductSubtype(value);
                                }}
                                onClear={() => setProductSubtype(undefined)}
                                productType={productType}
                                required={isFieldRequired(SUBTYPE)}
                                value={productSubtype}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextInput
                                dirty={hasBeenSubmitted}
                                endAdornment={getEndAdornment(NAME)}
                                handleError={(errorStatus: boolean, errorMessage: string) => {
                                    handleErrorOnInput(NAME, {errorStatus, errorMessage});
                                }}
                                helperText={fieldErrors[NAME]}
                                label="Product Name"
                                onChange={(value) => {
                                    updateDraftProduct(NAME, value);
                                }}
                                required={isFieldRequired(NAME)}
                                value={name}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextInput
                                dirty={hasBeenSubmitted}
                                endAdornment={getEndAdornment(BRAND_NAME)}
                                handleError={(errorStatus: boolean, errorMessage: string) => {
                                    handleErrorOnInput(BRAND_NAME, {errorStatus, errorMessage});
                                }}
                                helperText={fieldErrors[BRAND_NAME]}
                                label="Brand"
                                onChange={(value) => {
                                    updateDraftProduct(BRAND_NAME, value || '');
                                }}
                                required={isFieldRequired(BRAND_NAME)}
                                value={brandName}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} hidden={isFieldInvalid(CLASSIFICATION)}>
                            <ProductClassificationSelect
                                dirty={hasBeenSubmitted}
                                dropdownIndicator={getDropdownIndicator(CLASSIFICATION)}
                                handleError={(errorStatus: boolean, errorMessage: string) => {
                                    handleErrorOnInput(CLASSIFICATION, {errorStatus, errorMessage});
                                }}
                                helperText={fieldErrors[CLASSIFICATION]}
                                onChange={(value) => {
                                    updateDraftProduct(CLASSIFICATION, value);
                                }}
                                onClear={() => clearDraftProductField(CLASSIFICATION)}
                                required={isFieldRequired(CLASSIFICATION)}
                                value={classification}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3} hidden={isFieldInvalid(AMOUNT)}>
                            <NumberInput
                                type="number"
                                allowDecimals
                                decimalPlaces={2}
                                dirty={hasBeenSubmitted}
                                endAdornment={getEndAdornment(AMOUNT)}
                                handleError={(errorStatus: boolean, errorMessage: string) => {
                                    handleErrorOnInput(AMOUNT, {errorStatus, errorMessage});
                                }}
                                helperText={fieldErrors[AMOUNT]}
                                label="Amount"
                                minimum={0}
                                onChange={(value) => {
                                    updateDraftProduct(AMOUNT, value);
                                }}
                                required={isFieldRequired(AMOUNT)}
                                value={amount ? String(amount) : ''}
                            />
                        </Grid>
                        <Grid item hidden={isFieldInvalid(ProductField.UOM)} xs={6} sm={3}>
                            <SelectInput
                                required
                                dirty={hasBeenSubmitted}
                                dropdownIndicator={getDropdownIndicator(ProductField.UOM)}
                                handleError={(errorStatus: boolean, errorMessage: string) => {
                                    handleErrorOnInput(ProductField.UOM, {
                                        errorStatus,
                                        errorMessage,
                                    });
                                }}
                                helperText={fieldErrors[ProductField.UOM]}
                                label="UoM"
                                onChange={(value) => {
                                    updateDraftProduct(ProductField.UOM, value);
                                }}
                                onClear={() => clearDraftProductField(ProductField.UOM)}
                                options={getValidUomOptions()}
                                value={uom}
                            />
                        </Grid>
                        <Grid
                            item
                            hidden={isFieldInvalid(ProductDetailsField.EXTRACTION_METHOD)}
                            xs={12}
                            sm={6}
                        >
                            <SelectInput
                                dirty={hasBeenSubmitted}
                                dropdownIndicator={getDropdownIndicator(
                                    ProductDetailsField.EXTRACTION_METHOD
                                )}
                                handleError={(errorStatus: boolean, errorMessage: string) => {
                                    handleErrorOnInput(ProductDetailsField.EXTRACTION_METHOD, {
                                        errorStatus,
                                        errorMessage,
                                    });
                                }}
                                helperText={fieldErrors[ProductDetailsField.EXTRACTION_METHOD]}
                                label="Extraction Method"
                                onChange={(value) => {
                                    updateJSONField(
                                        DETAILS,
                                        ProductDetailsField.EXTRACTION_METHOD,
                                        value
                                    );
                                }}
                                options={_.values(ExtractionMethod)}
                                required={isFieldRequired(ProductDetailsField.EXTRACTION_METHOD)}
                                value={extractionMethod || ''}
                                onClear={() => {
                                    updateJSONField(
                                        DETAILS,
                                        ProductDetailsField.EXTRACTION_METHOD,
                                        undefined
                                    );
                                }}
                            />
                        </Grid>
                    </Grid>
                </Section>
                <Section title="Product Details">
                    <Grid container ref={PRODUCT_DETAILS} spacing={6}>
                        <Grid item xs={12} sm={9}>
                            <TextInput
                                dirty={hasBeenSubmitted}
                                endAdornment={getEndAdornment(ECOMMERCE_NAME)}
                                handleError={(errorStatus: boolean, errorMessage: string) => {
                                    handleErrorOnInput(ECOMMERCE_NAME, {errorStatus, errorMessage});
                                }}
                                helperText={fieldErrors[ECOMMERCE_NAME]}
                                label="Menu Title"
                                onChange={(value) => {
                                    updateDraftProduct(ECOMMERCE_NAME, value);
                                }}
                                required={isFieldRequired(ECOMMERCE_NAME)}
                                value={eCommerceName}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <NumberInput
                                type="number"
                                allowDecimals
                                decimalPlaces={6}
                                dirty={hasBeenSubmitted}
                                endAdornment={getEndAdornment(PRICE)}
                                handleError={(errorStatus: boolean, errorMessage: string) => {
                                    handleErrorOnInput(PRICE, {errorStatus, errorMessage});
                                }}
                                helperText={fieldErrors[PRICE]}
                                label={getPriceFieldLabel()}
                                minimum={0}
                                onChange={(value) => {
                                    updateDraftProduct(PRICE, value);
                                }}
                                required={isFieldRequired(PRICE)}
                                value={price ? String(price) : ''}
                            />
                        </Grid>
                        <Grid item xs={12} sm={9}>
                            <TextInput
                                dirty={hasBeenSubmitted}
                                endAdornment={getEndAdornment(SKU)}
                                handleError={(errorStatus: boolean, errorMessage: string) => {
                                    handleErrorOnInput(SKU, {errorStatus, errorMessage});
                                }}
                                helperText={fieldErrors[SKU]}
                                label="SKU"
                                onChange={(value) => {
                                    updateDraftProduct(SKU, value);
                                }}
                                required={isFieldRequired(SKU)}
                                value={sku}
                            />
                        </Grid>
                        <Grid item hidden={isFieldInvalid(SIZE)} xs={6} sm={3}>
                            <TextInput
                                dirty={hasBeenSubmitted}
                                endAdornment={getEndAdornment(SIZE)}
                                handleError={(errorStatus: boolean, errorMessage: string) => {
                                    handleErrorOnInput(SIZE, {errorStatus, errorMessage});
                                }}
                                helperText={fieldErrors[SIZE]}
                                label="Size"
                                onChange={(value) => {
                                    updateDraftProduct(SIZE, value);
                                }}
                                required={isFieldRequired(SIZE)}
                                value={size}
                            />
                        </Grid>
                        <Grid item xs={6} hidden={isFieldInvalid(THC_MG)}>
                            <NumberInput
                                type="number"
                                allowDecimals={false}
                                dirty={hasBeenSubmitted}
                                endAdornment={getEndAdornment(THC_MG)}
                                handleError={(errorStatus: boolean, errorMessage: string) => {
                                    handleErrorOnInput(THC_MG, {errorStatus, errorMessage});
                                }}
                                helperText={fieldErrors[THC_MG]}
                                label="Total THC"
                                minimum={0}
                                onChange={(value) => {
                                    updateJSONField(DETAILS, THC_MG, value);
                                }}
                                units={NumericalUnit.MG}
                                required={isFieldRequired(THC_MG)}
                                value={thcMg ? String(thcMg) : ''}
                            />
                        </Grid>
                        <Grid item hidden={isFieldInvalid(CBD_MG)} xs={6}>
                            <NumberInput
                                type="number"
                                allowDecimals={false}
                                dirty={hasBeenSubmitted}
                                endAdornment={getEndAdornment(CBD_MG)}
                                handleError={(errorStatus: boolean, errorMessage: string) => {
                                    handleErrorOnInput(CBD_MG, {errorStatus, errorMessage});
                                }}
                                helperText={fieldErrors[CBD_MG]}
                                label="Total CBD"
                                minimum={0}
                                onChange={(value) => {
                                    updateJSONField(DETAILS, CBD_MG, value);
                                }}
                                units={NumericalUnit.MG}
                                required={isFieldRequired(CBD_MG)}
                                value={cbdMg ? String(cbdMg) : ''}
                            />
                        </Grid>
                        <Grid item hidden={isFieldInvalid(DOSE_THC_MG)} xs={6}>
                            <NumberInput
                                type="number"
                                dirty={hasBeenSubmitted}
                                endAdornment={getEndAdornment(DOSE_THC_MG)}
                                handleError={(errorStatus: boolean, errorMessage: string) => {
                                    handleErrorOnInput(DOSE_THC_MG, {errorStatus, errorMessage});
                                }}
                                helperText={fieldErrors[DOSE_THC_MG]}
                                label="THC per Dose"
                                minimum={0}
                                onChange={(value) => {
                                    updateJSONField(DETAILS, DOSE_THC_MG, value);
                                }}
                                units={NumericalUnit.MG}
                                required={isFieldRequired(DOSE_THC_MG)}
                                value={doseThcMg ? String(doseThcMg) : ''}
                            />
                        </Grid>
                        <Grid item hidden={isFieldInvalid(DOSE_CBD_MG)} xs={6}>
                            <NumberInput
                                type="number"
                                dirty={hasBeenSubmitted}
                                endAdornment={getEndAdornment(DOSE_CBD_MG)}
                                handleError={(errorStatus: boolean, errorMessage: string) => {
                                    handleErrorOnInput(DOSE_CBD_MG, {errorStatus, errorMessage});
                                }}
                                helperText={fieldErrors[DOSE_CBD_MG]}
                                label="CBD per Dose"
                                minimum={0}
                                onChange={(value) => {
                                    updateJSONField(DETAILS, DOSE_CBD_MG, value);
                                }}
                                units={NumericalUnit.MG}
                                required={isFieldRequired(DOSE_CBD_MG)}
                                value={doseCbdMg ? String(doseCbdMg) : ''}
                            />
                        </Grid>
                        <Grid item hidden={isFieldInvalid(DOSES)} xs={6}>
                            <NumberInput
                                type="number"
                                dirty={hasBeenSubmitted}
                                endAdornment={getEndAdornment(DOSES)}
                                handleError={(errorStatus: boolean, errorMessage: string) => {
                                    handleErrorOnInput(DOSES, {errorStatus, errorMessage});
                                }}
                                helperText={fieldErrors[DOSES]}
                                label="Doses"
                                minimum={1}
                                onChange={(value) => {
                                    updateJSONField(DETAILS, DOSES, value);
                                }}
                                required={isFieldRequired(DOSES)}
                                value={doses ? String(doses) : ''}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextInput
                                dirty={hasBeenSubmitted}
                                endAdornment={getEndAdornment(EXTERNAL_ID)}
                                handleError={(errorStatus: boolean, errorMessage: string) => {
                                    handleErrorOnInput(EXTERNAL_ID, {errorStatus, errorMessage});
                                }}
                                helperText={fieldErrors[EXTERNAL_ID]}
                                label="External Id"
                                onChange={(value) => {
                                    updateDraftProduct(EXTERNAL_ID, value);
                                }}
                                required={isFieldRequired(EXTERNAL_ID)}
                                value={externalId}
                            />
                        </Grid>
                        {/* AZ */}
                        <Grid item hidden={isFieldInvalid(STRAIN)} xs={6}>
                            <TextInput
                                dirty={hasBeenSubmitted}
                                endAdornment={getEndAdornment(STRAIN)}
                                handleError={(errorStatus: boolean, errorMessage: string) => {
                                    handleErrorOnInput(STRAIN, {errorStatus, errorMessage});
                                }}
                                helperText={fieldErrors[STRAIN]}
                                label="Strain"
                                onChange={(value) => {
                                    updateJSONField(DETAILS, STRAIN, value);
                                }}
                                required={isFieldRequired(STRAIN)}
                                value={strain}
                            />
                        </Grid>
                        <Grid item hidden={isFieldInvalid(GROSS_WEIGHT)} xs={6}>
                            <NumberInput
                                type="number"
                                dirty={hasBeenSubmitted}
                                endAdornment={getEndAdornment(GROSS_WEIGHT)}
                                handleError={(errorStatus: boolean, errorMessage: string) => {
                                    handleErrorOnInput(GROSS_WEIGHT, {errorStatus, errorMessage});
                                }}
                                helperText={fieldErrors[GROSS_WEIGHT]}
                                label="Gross Weight"
                                minimum={0}
                                onChange={(value) => {
                                    updateJSONField(DETAILS, GROSS_WEIGHT, value);
                                }}
                                required={isFieldRequired(GROSS_WEIGHT)}
                                units={NumericalUnit.G}
                                value={grossWeight ? String(grossWeight) : ''}
                            />
                        </Grid>
                        <Grid item hidden={isFieldInvalid(NET_WEIGHT)} xs={6}>
                            <NumberInput
                                type="number"
                                dirty={hasBeenSubmitted}
                                endAdornment={getEndAdornment(NET_WEIGHT)}
                                handleError={(errorStatus: boolean, errorMessage: string) => {
                                    handleErrorOnInput(NET_WEIGHT, {errorStatus, errorMessage});
                                }}
                                helperText={fieldErrors[NET_WEIGHT]}
                                label="Net Weight"
                                minimum={0}
                                onChange={(value) => {
                                    updateJSONField(DETAILS, NET_WEIGHT, value);
                                }}
                                required={isFieldRequired(NET_WEIGHT)}
                                units={NumericalUnit.G}
                                value={netWeight ? String(netWeight) : ''}
                            />
                        </Grid>
                        <Grid item hidden={isFieldInvalid(TOTAL_WEIGHT)} xs={6}>
                            <NumberInput
                                type="number"
                                dirty={hasBeenSubmitted}
                                endAdornment={getEndAdornment(TOTAL_WEIGHT)}
                                handleError={(errorStatus: boolean, errorMessage: string) => {
                                    handleErrorOnInput(TOTAL_WEIGHT, {errorStatus, errorMessage});
                                }}
                                helperText={fieldErrors[TOTAL_WEIGHT]}
                                label="Total Flower Weight"
                                minimum={0}
                                onChange={(value) => {
                                    updateJSONField(DETAILS, TOTAL_WEIGHT, value);
                                }}
                                required={isFieldRequired(TOTAL_WEIGHT)}
                                units={NumericalUnit.G}
                                value={totalWeight || totalWeight === 0 ? String(totalWeight) : ''}
                            />
                        </Grid>
                        <Grid item hidden={isFieldInvalid(TOTAL_CONCENTRATE_WEIGHT)} xs={6}>
                            <NumberInput
                                type="number"
                                dirty={hasBeenSubmitted}
                                endAdornment={getEndAdornment(TOTAL_CONCENTRATE_WEIGHT)}
                                handleError={(errorStatus: boolean, errorMessage: string) => {
                                    handleErrorOnInput(TOTAL_CONCENTRATE_WEIGHT, {errorStatus, errorMessage});
                                }}
                                helperText={fieldErrors[TOTAL_CONCENTRATE_WEIGHT]}
                                label="Total Concentrate Weight"
                                minimum={0}
                                onChange={(value) => {
                                    updateJSONField(DETAILS, TOTAL_CONCENTRATE_WEIGHT, value);
                                }}
                                required={isFieldRequired(TOTAL_CONCENTRATE_WEIGHT)}
                                units={NumericalUnit.G}
                                value={totalConcentrateWeight ? String(totalConcentrateWeight) : ''}
                            />
                        </Grid>
                    </Grid>
                </Section>
                {!isNew && (
                    <Section title="Store Pricing">
                        <Grid container ref={PRICING_SECTION}>
                            <Grid container spacing={6}>
                                <Grid item xs={12} sm={12}>
                                    <SelectInput
                                        label="Pricing"
                                        onChange={(value) => setPriceType(value as PriceType)}
                                        onClear={() => setPriceType(DEFAULT_PRICE_TYPE)}
                                        options={PRICE_SELECT_OPTIONS}
                                        value={priceType}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    {getPriceInputElement(priceType)}
                                </Grid>
                                {assignedPrices && (
                                    <Grid item sm={12}>
                                        <div className="assign-price-info">
                                            <InfoOutlinedIcon />
                                            These assignments will not be updated in the matrix view
                                            until you save changes on this form
                                        </div>
                                    </Grid>
                                )}
                            </Grid>
                            <Grid container spacing={8}>
                                <Grid item sm={12}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>
                                                    <b>Store</b>
                                                </TableCell>
                                                <TableCell>
                                                    <b>Price</b>
                                                </TableCell>
                                                <TableCell />
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {activeProductStorePresence.map((presence, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell>{presence.storeName}</TableCell>
                                                    <TableCell>
                                                        {getPriceForStore(presence)}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Button
                                                            buttonClass={ButtonClass.PREFERRED}
                                                            text="Assign"
                                                            disabled={
                                                                shouldDisablePriceAssign() ||
                                                                disabledButtons[idx] ||
                                                                disabledButtons.all
                                                            }
                                                            onClick={() => {
                                                                assignPriceToStore(
                                                                    presence,
                                                                    idx,
                                                                    priceType
                                                                );
                                                                setDisabledButtons((prevState) => {
                                                                    const newState = {...prevState};
                                                                    newState[idx] = true;
                                                                    return newState;
                                                                });
                                                            }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Grid>

                                <Grid item container justifyContent="flex-end" sm={12}>
                                    <Grid item sm={4}>
                                        <Button
                                            buttonClass={ButtonClass.PREFERRED}
                                            text="Assign to all"
                                            disabled={
                                                shouldDisablePriceAssign() || disabledButtons.all
                                            }
                                            onClick={() => {
                                                assignPriceToAllStores();
                                                setDisabledButtons((prevState) => ({
                                                    ...prevState,
                                                    all: true,
                                                }));
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Section>
                )}
                <Section title="Attributes">
                    <Grid container ref={PRODUCT_ATTRIBUTES} spacing={6}>
                        <Grid item xs={12} sm={12}>
                            <TagInput
                                options={generalAttributesOptions}
                                dropdownIndicator={getDropdownIndicator(
                                    ProductAttributesField.GENERAL
                                )}
                                label="General"
                                onChange={(values) =>
                                    updateJSONField(
                                        ATTRIBUTES,
                                        ProductAttributesField.GENERAL,
                                        values
                                    )
                                }
                                placeholder="Add Attribute"
                                values={general || []}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <TagInput
                                dropdownIndicator={getDropdownIndicator(
                                    ProductAttributesField.FLAVOR
                                )}
                                label="Flavors"
                                onChange={(values) => updateJSONField(ATTRIBUTES, FLAVOR, values)}
                                options={flavorAttributesOptions}
                                placeholder="Add a Flavor"
                                values={flavor}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <TagInput
                                dropdownIndicator={getDropdownIndicator(
                                    ProductAttributesField.EFFECT
                                )}
                                label="Effects"
                                onChange={(values) => updateJSONField(ATTRIBUTES, EFFECT, values)}
                                options={effectAttributesOptions}
                                placeholder="Add an Effect"
                                values={effect}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <TagInput
                                dropdownIndicator={getDropdownIndicator(
                                    ProductAttributesField.INGREDIENT
                                )}
                                label="Ingredients"
                                onChange={(values) =>
                                    updateJSONField(ATTRIBUTES, INGREDIENT, values)
                                }
                                options={ingredientAttributesOptions}
                                placeholder="Add Ingredient"
                                values={ingredient}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <TagInput
                                dropdownIndicator={getDropdownIndicator(
                                    ProductAttributesField.INTERNAL
                                )}
                                label="Internal Tags"
                                onChange={(values) => updateJSONField(ATTRIBUTES, INTERNAL, values)}
                                options={internalAttributesOptions}
                                placeholder="Internal Tags"
                                values={internal}
                            />
                        </Grid>
                    </Grid>
                </Section>
                <Section title="Commerce">
                    <Grid container ref={COMMERCE_SECTION} spacing={6}>
                        <Grid item xs={12} sm={12}>
                            {/* BUG HERE: Not loading existing images... just recently uploaded ones */}
                            <ImageGui
                                setUploadedImages={setUploadedImages}
                                uploadedImages={uploadedImages}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <div className="description-container">
                                <div className="description-label">
                                    Product Description
                                    {getEndAdornment(DESCRIPTIONS)}
                                </div>
                                <textarea
                                    onChange={(event) => {
                                        const input = event.target.value;
                                        updateJSONField(DESCRIPTIONS, 'main', input);
                                    }}
                                    value={main || ''}
                                />
                            </div>
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <TreezSwitch
                                checked={product.visible === false}
                                label="Hide from the Menu"
                                onClick={() =>
                                    updateDraftProduct(
                                        VISIBLE,
                                        product.visible == null ? false : !product.visible
                                    )
                                }
                            />
                        </Grid>
                    </Grid>
                </Section>
                {!isNew && (
                    <Section title="History">
                        <Grid container ref={HISTORY_SECTION}>
                            <Grid container spacing={6}>
                                <Grid item xs={12} sm={12}>
                                    <Tabs
                                        value={tabValue}
                                        onChange={handleHistoryTabChange}
                                        variant="scrollable"
                                        scrollButtons="auto"
                                        aria-label="Catalog History Tabs"
                                    >
                                        <Tab label="Central Catalog" />
                                        {stores?.map((store) => {
                                            return <Tab label={store.name} />;
                                        })}
                                    </Tabs>
                                    <CatalogHistory
                                        value={tabValue}
                                        index={0}
                                        productId={product.id}
                                        catalogId={catalog?.id}
                                    />
                                    {stores?.map((store, idx) => {
                                        return (
                                            <CatalogHistory
                                                value={tabValue}
                                                index={idx + 1}
                                                productId={product.id}
                                                catalogId={store.catalogId}
                                            />
                                        );
                                    })}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Section>
                )}
            </Form>
        </div>
    );
};

export default ProductForm;
